import { Platform } from '@angular/cdk/platform';
import { ComponentPortal, PortalModule } from '@angular/cdk/portal';
import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, Inject, InjectionToken, Injector, Input, OnChanges, OnInit, inject } from '@angular/core';
import { ModalService } from '@indice/ng-components';
import { Observable } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { IPhoto, MediaType, Photo, PhotoResultSet, PortalApiService, PropertyDetails } from 'src/app/services/portal-api.service';
import { Toast, ToasterService } from 'src/app/services/toaster.service';
import { RequestWalkthroughModalComponent } from 'src/app/shared/components/request-walkthrough-modal/request-walkthrough-modal.coponent';
import {TranslocoDirective, TranslocoService} from '@jsverse/transloco';
import { ThreedViewerComponent } from 'src/app/shared/threed-viewer/threed-viewer.component';
import { HeroImageSwiperComponent } from './hero-image-swiper/hero-image-swiper.component';
import { ModalImageGalleryComponent } from './modal-image-gallery/modal-image-gallery.component';

export const DATA_INJECTION_TOKEN = new InjectionToken<PhotoResultSet | undefined>('DATA_INJECTION_TOKEN');
@Component({
  selector: 'app-hero-section',
  standalone: true,
  imports: [CommonModule, TranslocoDirective, PortalModule],
  templateUrl: './hero-section.component.html',
  styleUrl: './hero-section.component.css',
})
export class HeroSectionComponent implements OnInit, OnChanges {

  @Input() model = new PropertyDetails();
  @Input()
  set photos(value: PhotoResultSet | undefined) {
    this._photos = value;
    this.floorPlanExists = this.photos?.items?.some(x => x.type === MediaType.PropertyElevation) == true;
    this.photosLoaded = true;
    this.checkAndCreatePortal();
  }
  
  get photos(): PhotoResultSet | undefined {
    return this._photos;
  }

  hasPreviousPage?: boolean
  private _photos: PhotoResultSet | undefined;
  vrCode: string | undefined;
  toast$: Observable<Toast> = new Observable();

  public floorPlanExists = false;
  protected activeLang: string = 'el';

  placeholderUri: string = '../../../../../assets/img/uniko-gray-logo.svg';

  swiperPortal!: ComponentPortal<HeroImageSwiperComponent>;

  private readonly destroyRef = inject(DestroyRef);

  private modelLoaded = false;
  private photosLoaded = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private modalService: ModalService,
    private _location: Location,
    private _authService: AuthService,
    @Inject(ToasterService) private _toastService: ToasterService,
    private portalService: PortalApiService,
    @Inject(Platform) public readonly platform: Platform,
    private translocoService: TranslocoService) { }
  ngOnInit() {
    this.translocoService.langChanges$.subscribe(lang => {
      this.activeLang = lang;
    })
    if (this.model?.defaultPhoto) {
      this.model.defaultPhoto.uri = this.model.defaultPhoto.photoItems?.find(pi => pi.uri?.endsWith('-l.webp'))?.uri ?? undefined
    }
    this.hasPreviousPage = window.history.length > 1;

    this.portalService.getThreeDCode(this.model.id ?? '').subscribe({
      next: (result) => { this.vrCode = result?.spaceId }
    })
  }

  ngOnChanges() {
    if (this.model) {
      this.modelLoaded = true;
      this.checkAndCreatePortal();
    }
  }

  private checkAndCreatePortal() {
    if (this.modelLoaded || this.photosLoaded) {
      if (this.model && this._photos) {
          this.swiperPortal = new ComponentPortal(
            HeroImageSwiperComponent,
            null,
            Injector.create({
              providers: [{ provide: DATA_INJECTION_TOKEN, useValue: { photos: this._photos, model: this.model, floorPlanExists: this.floorPlanExists, combinedArray: [this._photos, this.model] } }]
            })
          );
      }
    }
  }

  getDisplayItems(): Photo[] {
    const displayItems: Photo[] = [];
    for (let i = 0; i < 4; i++) {
      let photo: Photo | undefined = this.photos?.items?.[i];
      if (photo) {
        if (!photo.isDefault && Array.isArray(photo.photoItems)) {
          // Find the photoItem with URI ending in -l.webp
          const smallWebpUri = photo.photoItems?.find(f => f?.uri?.endsWith('-m.webp'))?.uri;

          if (smallWebpUri) {
            // Replace the uri with the small webp version
            photo = new Photo({
              ...photo,
              uri: smallWebpUri
            });
          }
        }
        displayItems.push(photo);
      } else {
        displayItems.push(new Photo({ uri: this.placeholderUri, altText: '' })); // Placeholder item
      }
    }
    return displayItems;
  }

  isPlaceholder(uri: string | undefined): boolean {
    return uri === this.placeholderUri;
  }

  openModalComponent(event?: MouseEvent) {
    const imageGalleryModal = this.modalService.show(ModalImageGalleryComponent, {
      class: 'h-dvh md:h-auto',
      initialState: {
        isSold:this.model.isSold ?? false,
        title: `${this.model?.propertyArea}`,
        photos: this.photos,
        property: this.model
      },
      keyboard: true,
    });
    imageGalleryModal.onHidden?.subscribe((response: any) => {

    });
  }
  openFloorPlanComponent() {
    const items = this.photos?.items?.filter(x => x.type == MediaType.PropertyElevation);
    const imageGalleryModal = this.modalService.show(ModalImageGalleryComponent, {
      animated: true,
      initialState: {
        title: this.model?.title,
        photos: new PhotoResultSet({
          items: items,
          count: items?.length
        })
      },
      keyboard: true,
      class: 'h-dvh md:h-auto',
    });
    imageGalleryModal.onHidden?.subscribe((response: any) => {

    });
  }
  openVrModalComponent() {
    const vrModal = this.modalService.show(ThreedViewerComponent, {
      animated: true,
      initialState: {
        SpaceId: this.vrCode
      },
      keyboard: true,
      class: 'h-dvh md:h-auto',
    });
    vrModal.onHidden?.subscribe((response: any) => {

    });
  }
  GoBack() {
    if(!this.hasPreviousPage) return;
    this._location.back();
  }

  protected resolvePhotoSrc(): string | undefined {
    return '';
  }
}
